// Panel "Elementos": agregar formas, agrupadas por categoría y en grilla de 2
// columnas (cards más grandes, estilo Canva). El ícono de cada card ES la forma
// real (mismo polyPoints que el render), así no depende de iconos externos.

import { makeShape } from '../lib/factory';
import { polyPoints } from '../lib/shapes';
import { PanelSheet } from './PanelSheet';
import type { ShapeKind } from '../scene/types';
import type { SceneApi } from '../scene/useScene';

const SHAPE_LABEL: Record<ShapeKind, string> = {
  rect: 'Rectángulo',
  circle: 'Círculo',
  triangle: 'Triángulo',
  pentagon: 'Pentágono',
  hexagon: 'Hexágono',
  star: 'Estrella',
  line: 'Línea',
  arrow: 'Flecha',
};

const CATEGORIES: { title: string; kinds: ShapeKind[] }[] = [
  { title: 'Básicas', kinds: ['rect', 'circle', 'triangle'] },
  { title: 'Líneas', kinds: ['line', 'arrow'] },
  { title: 'Polígonos', kinds: ['pentagon', 'hexagon'] },
  { title: 'Estrellas', kinds: ['star'] },
];

// En mobile el sheet es full-width, así que cada card de la grilla de 2 columnas mide
// ~185px y un ícono de 32px quedaba perdido en el medio. Base = mobile (64px), `md:`
// vuelve al tamaño de desktop, donde el panel es una columna angosta de 280px.
const ICON_CLS = 'w-16 h-16 md:w-8 md:h-8';

function ShapeIcon({ kind }: { kind: ShapeKind }) {
  if (kind === 'circle') {
    return (
      <svg viewBox="0 0 24 24" className={ICON_CLS}>
        <circle cx="12" cy="12" r="10" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'rect') {
    return (
      <svg viewBox="0 0 24 24" className={ICON_CLS}>
        <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'line') {
    return (
      <svg viewBox="0 0 24 24" className={ICON_CLS}>
        <rect x="2" y="10.75" width="20" height="2.5" rx="1.25" fill="currentColor" />
      </svg>
    );
  }
  const pts = polyPoints(kind);
  if (pts) {
    const p = pts.map(([x, y]) => `${2 + x * 20},${2 + y * 20}`).join(' ');
    return (
      <svg viewBox="0 0 24 24" className={ICON_CLS}>
        <polygon points={p} fill="currentColor" />
      </svg>
    );
  }
  return null;
}

interface Props {
  api: SceneApi;
  onClose: () => void;
}

export function ElementosPanel({ api, onClose }: Props) {
  return (
    <PanelSheet title="Elementos" onClose={onClose}>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
        {CATEGORIES.map((cat) => (
          <div key={cat.title}>
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">{cat.title}</p>
            <div className="grid grid-cols-2 gap-2">
              {cat.kinds.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => api.addLayer(makeShape(kind, api.scene.ratio))}
                  className="aspect-square rounded-md border border-neutral-200 hover:border-brand-primary/40 hover:bg-surface-elevated flex flex-col items-center justify-center gap-2 text-neutral-600 active:scale-95 transition"
                >
                  <ShapeIcon kind={kind} />
                  <span className="text-[13px] md:text-[11px]">{SHAPE_LABEL[kind]}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PanelSheet>
  );
}
